import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import EmptyState from '../../ui/EmptyState'
import Loader from '../../shared/Loader'
import EvolutionAreaChart from '../EvolutionAreaChart'
import ProgressionService from '../../../services/api/ProgressionService'

const CARD_CLASSES = 'tw:border tw:border-solid tw:border-gray-300 tw:rounded-md! '
  + 'tw:bg-white tw:overflow-hidden tw:shrink-0'

const HEADER_BASE = 'tw:flex tw:items-center tw:justify-between tw:gap-3 tw:w-full '
  + 'tw:px-4 tw:py-3 tw:border-0 tw:cursor-pointer '
  + 'tw:text-left tw:text-ds-neutral-highest tw:text-sm tw:font-medium'

const BODY_WRAPPER = 'tw:grid tw:transition-[grid-template-rows] tw:duration-200 tw:ease-out'

const EMPTY_MESSAGE = 'Les récapitulatifs de vos sections apparaîtront ici une fois les données disponibles.'

const hasNoValue = bucket => bucket.value === null || typeof bucket.value === 'undefined'

const buildParams = ({
  dateRange, granularity, operatorId, resourceIds, resourceSearch, variableId, questionId
}) => ({
  startDate: dateRange.startDate,
  endDate: dateRange.endDate,
  granularity,
  operatorId: operatorId || null,
  resourceIds,
  resourceSearch: resourceSearch || null,
  variableId: variableId || null,
  questionId
})

const QuestionAccordion = ({
  question, isOpen, onToggle, collectId, gradeMax, mode, dateRange, granularity,
  operatorId, resourceIds, resourceSearch, variableId
}) => {
  const [buckets, setBuckets] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [hasBeenOpen, setHasBeenOpen] = useState(false)

  useEffect(() => { if (isOpen)
    setHasBeenOpen(true) }, [isOpen])

  const handleClick = useCallback(() => onToggle(question.id), [onToggle, question.id])

  useEffect(() => {
    let cancelled = false
    if (isOpen) {
      const measureName = mode === 'grade' ? 'question_grade' : 'question_satisfaction'
      setIsLoading(true)
      ProgressionService.fetchMeasure(collectId, null, {
        name: measureName,
        ...buildParams({
          dateRange,
          granularity,
          operatorId,
          resourceIds,
          resourceSearch,
          variableId,
          questionId: question.id
        })
      })
        .then(result => { if (!cancelled)
          setBuckets(result || []) })
        .catch(() => { if (!cancelled)
          setBuckets([]) })
        .finally(() => {
          if (cancelled)
            return
          setIsLoading(false)
          setHasLoaded(true)
        })
    }
    return () => { cancelled = true }
  }, [
    isOpen,
    collectId,
    mode,
    dateRange,
    granularity,
    operatorId,
    resourceIds,
    resourceSearch,
    variableId,
    question.id
  ])

  const showLoader = isLoading && !hasLoaded
  const showChart = hasLoaded || !isLoading
  const isEmpty = hasLoaded && (buckets.length === 0 || buckets.every(hasNoValue))
  const chartProps = { buckets, gradeMax, mode, hideToolbar: true, compactLabels: true }

  return (
    <div className={CARD_CLASSES}>
      <button
        aria-expanded={isOpen}
        className={`${HEADER_BASE} ${isOpen ? 'tw:bg-gray-100' : 'tw:bg-white'}`}
        type="button"
        onClick={handleClick}
      >
        <span className="tw:grow">{question.title}</span>
        <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'} tw:text-ds-neutral-high`} />
      </button>
      <div className={`${BODY_WRAPPER} ${isOpen ? 'tw:grid-rows-[1fr]' : 'tw:grid-rows-[0fr]'}`}>
        <div className="tw:overflow-hidden">
          {hasBeenOpen ? (
            <div className="tw:p-4 tw:border-t tw:border-solid tw:border-gray-300">
              {showLoader ? <Loader showText={false} size="small" /> : null}
              {isEmpty ? <EmptyState message={EMPTY_MESSAGE} /> : null}
              {showChart && !isEmpty ? <EvolutionAreaChart {...chartProps} /> : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

QuestionAccordion.propTypes = {
  collectId: PropTypes.number.isRequired,
  dateRange: PropTypes.shape({
    endDate: PropTypes.string,
    startDate: PropTypes.string
  }).isRequired,
  gradeMax: PropTypes.number.isRequired,
  granularity: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(['grade', 'satisfaction']).isRequired,
  onToggle: PropTypes.func.isRequired,
  operatorId: PropTypes.string,
  question: PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired
  }).isRequired,
  resourceIds: PropTypes.arrayOf(PropTypes.number),
  resourceSearch: PropTypes.string,
  variableId: PropTypes.string
}

QuestionAccordion.defaultProps = {
  operatorId: '',
  resourceIds: [],
  resourceSearch: '',
  variableId: ''
}

export default QuestionAccordion
